import { Button } from "@material-ui/core";
import clsx from "clsx";
import { format, isAfter } from "date-fns";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import React, { useMemo } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { DialogEnum } from "@/consts/DialogEnum";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { DialogViewEnum, useDialog } from "@/features/common/hooks/useDialog";
import { useI18n } from "@/features/common/hooks/useI18n";
import { useButtonStyles } from "@/features/common/styles";
import { PostMaskSubscribeButton } from "@/features/post/components/PostMaskSubscribeButton";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  PostInfoWithLoggedInUserFragment,
  PostPagePostFragment,
  PostTypeEnum,
} from "@/generated/generated-hooks";
import { isThisMonthTz } from "@/helpers/common/dateTimeHelper";

type HoverSubscribeMaskProps = {
  post: PostPagePostFragment & Partial<PostInfoWithLoggedInUserFragment>;
  logEventSource?: "image-card" | "post-carousel";
};

export const HoverSubscribeMask: React.FC<HoverSubscribeMaskProps> = ({
  post,
  logEventSource,
}) => {
  const { t } = useTranslation("post");
  const { openDialog: openUIDialog } = useUIContext();
  const { openDialog } = useDialog();
  const buttonClasses = useButtonStyles();
  const { user, ready } = useUserContext();
  const { formatDistanceToNowStrictWithLocale } = useI18n();

  const isVideo = post.postType === PostTypeEnum.Video;
  const { _id, slug, publishAt, isPublished, createdAt } = post;

  const publishDate = useMemo(() => {
    return publishAt ? new Date(publishAt) : null;
  }, [publishAt]);

  const isLocked = useMemo(() => {
    const {
      isContentLocked,
      postBlurLevel,
      isUserContentLocked,
      userBlurLevel,
    } = post;

    return (
      (userBlurLevel === undefined && (postBlurLevel > 0 || isContentLocked)) ||
      userBlurLevel > 0 ||
      isUserContentLocked
    );
  }, [post]);

  const notPublishedYet = useMemo(() => {
    return !isPublished && publishAt && isAfter(publishDate, new Date());
  }, [isPublished, publishAt, publishDate]);

  const isThisMonth = useMemo(() => {
    if (!createdAt && !publishAt) return true;
    const createdDate = new Date(publishAt ?? createdAt);
    return isThisMonthTz(createdDate);
  }, [createdAt, publishAt]);

  if (
    !ready ||
    post?.userBlurLevel === undefined ||
    post?.isUserContentLocked === undefined
  )
    return null;
  const isOwner = post.writer._id === user?._id;

  // if !isPublished -> display publishing soon mask
  // if user logged in --> show the corresponding subscription dialogue
  // if video post visit by signed in user
  if (
    !notPublishedYet &&
    (isOwner || !isLocked) &&
    !(isVideo && !post.videoUri && !user)
  ) {
    return null;
  }

  function handleClick(e) {
    e.stopPropagation();

    if (notPublishedYet) return;

    if (!user) {
      firebase.analytics().logEvent(AnalyticsEventEnum.HoverSubscribeMask, {
        postId: post._id,
        postSlug: post.slug,
        source: logEventSource,
      });

      openUIDialog(DialogEnum.Login);

      return;
    }

    firebase
      .analytics()
      .logEvent(
        isThisMonth
          ? AnalyticsEventEnum.ImageCardClickSubscribe
          : AnalyticsEventEnum.ImageCardPurchase,
        { postId: _id, postSlug: slug },
      );

    openDialog({
      view: DialogViewEnum.SubscriptionPlanDialog,
      writer: post.writer,
      post,
      channel: undefined,
    });
  }

  return (
    <div
      className="w-full h-full flex justify-center items-center bg-black bg-opacity-30"
      onClick={handleClick}
    >
      <div className="flex flex-col items-center text-white">
        <div className="flex items-center justify-center">
          <Image
            priority
            alt="post is locked"
            layout="fixed"
            width={isVideo ? 50 : 37}
            height={isVideo ? 50 : 42}
            src={isVideo ? "/assets/post/video.png" : "/assets/post/lock.png"}
          />
        </div>

        {notPublishedYet ? (
          <span className="my-4">
            {t("imageCard.publishAt", {
              publishTime: format(publishDate, "dd/MM/yyyy HH:mm"),
            })}
          </span>
        ) : isVideo && !post.videoUri && !user ? (
          <Button
            variant="outlined"
            className={clsx(buttonClasses.outlinedWhite, "mx-3")}
            onClick={handleClick}
          >
            {t("post:imageCard.guestHoverButtonLabel")}
          </Button>
        ) : user ? (
          <PostMaskSubscribeButton
            variant="image"
            post={post}
            onClick={handleClick}
          />
        ) : (
          <div className="my-2">
            <Button
              variant="outlined"
              className={clsx(buttonClasses.outlinedWhite, "mx-3")}
              onClick={handleClick}
            >
              {t("post:imageCard.guestHoverButtonLabel")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
